import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { DhlAuthInfo, DhlAuthStatus } from '../services/dhl-onboarding-data';
import { DhlOnboardingService } from '../services/dhl-onboarding.service';

@Component({
    selector: 'phx-dhl-onboarding',
    templateUrl: './dhl-onboarding.component.html',
    styleUrls: ['./dhl-onboarding.component.scss'],
    imports: [NgbAlert, DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DhlOnboardingComponent implements OnInit {
    readonly loading = signal(false);
    readonly isButtonClickLoading = signal(false);
    readonly hasError = signal(false);
    readonly hasCallbackError = signal(false);
    readonly disableButton = signal(false);
    readonly dhlAuthInfo = signal<DhlAuthInfo | null>(null);

    private destroyRef = inject(DestroyRef);

    constructor(
        private dhlOnboardingService: DhlOnboardingService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private titleService: Title
    ) {}

    ngOnInit(): void {
        this.titleService.setTitle(this.activatedRoute.snapshot.data?.['title']);

        this.handleOAuthCallbackIfPresent();
    }

    connectToDhl() {
        this.isButtonClickLoading.set(true);
        this.hasError.set(false);
        this.disableButton.set(true);
        this.dhlOnboardingService
            .getDhlLoginUrl()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: res => {
                    this.isButtonClickLoading.set(false);
                    this.disableButton.set(false);
                    window.location.href = res.returnUrl;
                },
                error: () => {
                    this.isButtonClickLoading.set(false);
                    this.hasError.set(true);
                    this.disableButton.set(false);
                }
            });
    }

    private handleOAuthCallbackIfPresent() {
        const queryParams = this.activatedRoute.snapshot.queryParamMap;
        const code = queryParams.get('code');
        const state = queryParams.get('state');
        const sessionState = queryParams.get('session_state');
        const iss = queryParams.get('iss');

        if (!code || !state || !sessionState || !iss) {
            this.initDhlAuthInfo();
            return;
        }

        this.loading.set(true);
        this.hasError.set(false);
        this.hasCallbackError.set(false);
        this.dhlOnboardingService
            .completeDhlLogin({ code, state, sessionState, iss })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => this.finishOAuthCallback(),
                error: () => {
                    this.hasCallbackError.set(true);
                    this.finishOAuthCallback();
                }
            });
    }

    private finishOAuthCallback() {
        // remove code/state from the url so a refresh does not re-trigger the token exchange
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {},
            replaceUrl: true
        });

        this.initDhlAuthInfo();
    }

    private initDhlAuthInfo() {
        this.loading.set(true);
        this.hasError.set(false);
        this.dhlOnboardingService
            .getDhlAuthInfo()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: dhlAuthInfo => {
                    this.dhlAuthInfo.set(dhlAuthInfo);
                    this.loading.set(false);
                },
                error: () => {
                    // keep the page usable (e.g. on 401/500) instead of leaving the spinner running forever
                    this.dhlAuthInfo.set(null);
                    this.hasError.set(true);
                    this.loading.set(false);
                }
            });
    }

    statusLabel(status: DhlAuthStatus | undefined): string {
        switch (status) {
            case 'OK':
                return 'Verbunden';
            case 'EXPIRED':
                return 'Abgelaufen';
            default:
                return 'Nicht verbunden';
        }
    }

    statusInfo(status: DhlAuthStatus | undefined): string {
        switch (status) {
            case 'OK':
                return 'Ihr Händlerkonto ist mit DHL verbunden.';
            case 'EXPIRED':
                return 'Ihr Händlerkonto ist mit DHL verbunden, aber das Token ist abgelaufen.';
            default:
                return 'Ihr Händlerkonto ist nicht mit DHL verbunden.';
        }
    }
}
